import React from 'react';

const Refund = () => {
  return (
    <div className="main">
      <div className="container">
        <div className="legal-content">
          <h1>Refund Policy</h1>
          
          <div className="legal-highlight-box">
            <p><strong>Fair Refund Promise:</strong> At CHALYATI, we believe in fair and transparent refund policies. We understand that plans can change, and we're committed to providing reasonable refund options while maintaining our service quality.</p>
          </div>
          
          <h2>1. Cancellation and Refund Eligibility</h2>
          <p>Refunds are available for cancelled bookings according to our structured cancellation policy. The refund amount and processing time depend on when the cancellation is made relative to your scheduled pickup time and the specific circumstances of your booking.</p>
          
          <div className="legal-section-divider"></div>
          
          <h2>2. Cancellation Timeframes</h2>
          <p>Our cancellation policy is designed to be fair to both customers and our business operations:</p>
          
          <div className="legal-highlight-box">
            <p><strong>🟢 Free Cancellation (100% Refund):</strong><br/>
            Cancellations made <strong>24 hours or more</strong> before the scheduled pickup time are eligible for a full refund of the total rental amount, excluding any processing fees.</p>
          </div>
          
          <ul>
            <li><strong>🟡 Partial Refund (50% Refund):</strong> Cancellations made between 2-24 hours before pickup receive a 50% refund of the total rental amount</li>
            <li><strong>🔴 No Refund:</strong> Cancellations made less than 2 hours before pickup or no-shows are not eligible for refunds</li>
            <li><strong>⚪ Same-Day Bookings:</strong> Special terms apply for bookings made within 24 hours of pickup time</li>
          </ul>
          
          <h2>3. Refund Processing Timeline</h2>
          <p>We strive to process refunds as quickly as possible:</p>
          <ul>
            <li><strong>Approval Time:</strong> 1-2 business days for refund approval</li>
            <li><strong>Credit Card Refunds:</strong> 5-10 business days after approval</li>
            <li><strong>Debit Card Refunds:</strong> 7-14 business days after approval</li>
            <li><strong>Bank Transfer Refunds:</strong> 3-7 business days after approval</li>
            <li><strong>Digital Wallet Refunds:</strong> 1-3 business days after approval</li>
          </ul>
          <p>Refunds are always processed back to the original payment method used for the booking.</p>
          
          <div className="legal-section-divider"></div>
          
          <h2>4. Early Return Refunds</h2>
          <p>If you return a vehicle earlier than the scheduled return date, you may be eligible for a partial refund:</p>
          <ul>
            <li><strong>Minimum Rental Period:</strong> First 4 hours are non-refundable</li>
            <li><strong>Pro-rata Calculation:</strong> Refunds calculated on unused full days only</li>
            <li><strong>Processing Time:</strong> 3-5 business days for early return refunds</li>
            <li><strong>Notification Required:</strong> Must inform us at least 2 hours before early return</li>
          </ul>
          
          <div className="legal-highlight-box">
            <p><strong>Early Return Example:</strong> If you book a 5-day rental but return after 3 days, you may receive a refund for 2 unused days (subject to terms and conditions).</p>
          </div>
          
          <h2>5. Vehicle Unavailability Refunds</h2>
          <p>When we cannot provide your booked vehicle due to circumstances beyond your control:</p>
          <ul>
            <li><strong>Mechanical Issues:</strong> Full refund or free upgrade to available vehicle</li>
            <li><strong>Accident Damage:</strong> Full refund or equivalent vehicle at no extra cost</li>
            <li><strong>Maintenance Requirements:</strong> Alternative vehicle or full refund</li>
            <li><strong>Fleet Shortages:</strong> Priority rebooking or immediate full refund</li>
          </ul>
          <p>In all cases, we will contact you immediately and offer the best available solution, including compensation for any inconvenience caused.</p>
          
          <div className="legal-section-divider"></div>
          
          <h2>6. Weather and Force Majeure</h2>
          <p>We understand that extraordinary circumstances can disrupt travel plans:</p>
          <ul>
            <li><strong>Severe Weather:</strong> Full refund for unsafe driving conditions</li>
            <li><strong>Natural Disasters:</strong> Complete refund with no penalties</li>
            <li><strong>Government Restrictions:</strong> Full refund for travel bans or lockdowns</li>
            <li><strong>Emergency Situations:</strong> Case-by-case evaluation with maximum flexibility</li>
          </ul>
          <p>We monitor weather conditions and government advisories to proactively reach out to affected customers.</p>
          
          <h2>7. Service Quality Refunds</h2>
          <p>Your satisfaction is our priority. If you experience significant service quality issues:</p>
          <ul>
            <li><strong>Vehicle Condition Issues:</strong> Partial to full refund based on severity</li>
            <li><strong>Service Delays:</strong> Compensation or refund for time lost</li>
            <li><strong>Staff Misconduct:</strong> Full investigation and appropriate compensation</li>
            <li><strong>Booking Errors:</strong> Immediate correction or full refund</li>
          </ul>
          
          <div className="legal-highlight-box">
            <p><strong>Report Issues Immediately:</strong> All service quality issues must be reported during your rental period or within 24 hours of return for fastest resolution.</p>
          </div>
          
          <div className="legal-section-divider"></div>
          
          <h2>8. Refund Exclusions</h2>
          <p>The following charges are non-refundable under any circumstances:</p>
          <ul>
            <li><strong>Late Return Fees:</strong> Charges for returns beyond the grace period</li>
            <li><strong>Fuel Charges:</strong> Additional costs for insufficient fuel on return</li>
            <li><strong>Cleaning Fees:</strong> Charges for excessive cleaning requirements</li>
            <li><strong>Damage Charges:</strong> Costs for vehicle damage during rental</li>
            <li><strong>Traffic Violations:</strong> Fines and penalties incurred during rental</li>
            <li><strong>Insurance Premiums:</strong> Optional insurance coverage fees</li>
            <li><strong>Add-on Services:</strong> GPS, child seats, and other extras</li>
            <li><strong>Processing Fees:</strong> Administrative and payment processing charges</li>
          </ul>
          
          <h2>9. Third-Party Booking Refunds</h2>
          <p>For bookings made through third-party platforms or travel agents:</p>
          <ul>
            <li>Refund policies of the booking platform apply</li>
            <li>We will cooperate with the platform for legitimate refund requests</li>
            <li>Direct refund requests must be made through the original booking channel</li>
            <li>Platform fees and commissions are subject to their terms</li>
          </ul>
          
          <div className="legal-section-divider"></div>
          
          <h2>10. Refund Request Process</h2>
          <p>To request a refund, please follow these simple steps:</p>
          <ul>
            <li><strong>Step 1:</strong> Contact our customer service within 30 days of the rental</li>
            <li><strong>Step 2:</strong> Provide your booking reference number and contact details</li>
            <li><strong>Step 3:</strong> Explain the reason for your refund request in detail</li>
            <li><strong>Step 4:</strong> Submit any required supporting documentation</li>
            <li><strong>Step 5:</strong> Await our response within 48 hours</li>
          </ul>
          
          <h2>11. Documentation Requirements</h2>
          <p>Depending on your refund request, you may need to provide:</p>
          <ul>
            <li><strong>Medical Emergencies:</strong> Medical certificates or hospital records</li>
            <li><strong>Travel Restrictions:</strong> Official government notifications</li>
            <li><strong>Vehicle Issues:</strong> Photos or video evidence</li>
            <li><strong>Service Problems:</strong> Detailed incident reports</li>
            <li><strong>Weather Claims:</strong> Official weather advisories</li>
          </ul>
          <p>We may request additional documentation to process your refund fairly and efficiently.</p>
          
          <div className="legal-section-divider"></div>
          
          <h2>12. Dispute Resolution</h2>
          <p>If you disagree with a refund decision:</p>
          <ul>
            <li><strong>Internal Review:</strong> Request escalation to our customer relations team</li>
            <li><strong>Detailed Review:</strong> Comprehensive case evaluation within 10 business days</li>
            <li><strong>Final Decision:</strong> Written explanation of the final determination</li>
            <li><strong>External Mediation:</strong> Independent mediation services if needed</li>
          </ul>
          
          <div className="legal-highlight-box">
            <p><strong>We're Here to Help:</strong> Our goal is to resolve all refund disputes fairly and maintain positive customer relationships. We encourage open communication throughout the process.</p>
          </div>
          
          <h2>13. Refund Abuse Prevention</h2>
          <p>To maintain fair pricing for all customers, we monitor for refund abuse:</p>
          <ul>
            <li>Frequent cancellations may result in booking restrictions</li>
            <li>False claims will result in account suspension</li>
            <li>Fraudulent documentation may lead to legal action</li>
            <li>Repeat offenders may be permanently banned</li>
          </ul>
          
          <h2>14. Special Circumstances</h2>
          <p>We understand that unique situations may arise. For circumstances not covered in this policy:</p>
          <ul>
            <li>Contact our customer service for individual case review</li>
            <li>Provide detailed explanation of your situation</li>
            <li>We will evaluate each case on its merits</li>
            <li>Decisions will be made with fairness and empathy</li>
          </ul>
          
          <div className="legal-contact-info">
            <h3>Refund Support Contact</h3>
            <p>For refund requests and related inquiries:</p>
            <p><strong>Refund Email:</strong> refunds@chalyati.com</p>
            <p><strong>Customer Service:</strong> support@chalyati.com</p>
            <p><strong>Phone:</strong> +91 80996 62446</p>
            <p><strong>WhatsApp:</strong> +91 80996 62446 (Quick Support)</p>
            <p><strong>Office Hours:</strong> Monday to Sunday, 8:00 AM - 10:00 PM IST</p>
          </div>
          
          <div className="legal-last-updated">
          <p><strong>Last updated:</strong> December 2024</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;